import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import { useTransactions, useAccounts } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const typeIcons = {
  checking: '🏦',
  savings: '💰',
  brokerage: '📈',
  retirement: '🪙',
  crypto: '🪙',
  'liab-cc': '💳',
};

const categoryIcons = {
  'Rent': '🏠',
  'Groceries': '🛒',
  'Dining Out': '🍽️',
  'Gas': '⛽',
  'Auto Loan': '🚗',
  'Student Loan': '🎓',
  'Utilities': '💡',
  'Phone': '📱',
  'Internet': '🌐',
  'Subscriptions': '📺',
  'Shopping': '🛍️',
  'Salary': '💰',
  'Savings': '💳',
  'Investments': '📈',
  'Transfer': '↔️',
  'Auto Insurance': '🚗',
  'Entertainment': '🎬',
  'Travel': '✈️',
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const getRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const TransactionRow = ({ transaction, account }) => {
  const isCredit = transaction.type === 'credit' || transaction.amount > 0;
  const amount = Math.abs(transaction.amount);

  return (
    <View style={styles.transactionRow}>
      <View style={styles.transactionInfo}>
        <Text style={styles.transactionIcon}>
          {categoryIcons[transaction.category] || '💳'}
        </Text>
        <View style={styles.transactionDetails}>
          <Text style={styles.merchantName}>{transaction.merchant}</Text>
          <Text style={styles.categoryName}>{transaction.category}</Text>
        </View>
      </View>
      <View style={styles.transactionMeta}>
        <Text style={styles.transactionDate}>{formatDate(transaction.date)}</Text>
        <Text style={styles.transactionRelativeDate}>{getRelativeDate(transaction.date)}</Text>
      </View>
      <View style={styles.accountInfo}>
        <Text style={styles.accountIcon}>
          {typeIcons[account?.type] || typeIcons[transaction.account_id] || '💼'}
        </Text>
        <Text style={styles.accountName}>
          {account?.name || transaction.account_id.replace('acc-', '').replace('liab-', '')}
        </Text>
      </View>
      <View style={styles.amountContainer}>
        <Text style={[
          styles.transactionAmount,
          isCredit ? styles.creditAmount : styles.debitAmount
        ]}>
          {isCredit ? '+' : '-'}${amount.toLocaleString()}
        </Text>
        <Text style={styles.transactionType}>{transaction.type}</Text>
      </View>
      <TouchableOpacity style={styles.detailsButton}>
        <Text style={styles.detailsButtonText}>Details</Text>
      </TouchableOpacity>
    </View>
  );
};

const Transactions = () => {
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();
  const { data: accounts, loading: accountsLoading, error: accountsError, refresh: refreshAccounts } = useAccounts();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedAccount, setSelectedAccount] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [dateRange, setDateRange] = useState('All');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const loading = transactionsLoading || accountsLoading;
  const error = transactionsError || accountsError;
  const refresh = () => {
    refreshTransactions();
    refreshAccounts();
  };

  // Create accounts lookup
  const accountsMap = useMemo(() => {
    if (!accounts) return {};
    return accounts.reduce((acc, account) => {
      acc[account.id] = account;
      return acc;
    }, {});
  }, [accounts]);

  // Get unique values for filters
  const categories = useMemo(() => {
    if (!transactions) return [];
    return [...new Set(transactions.map(t => t.category))].sort();
  }, [transactions]);

  const accountOptions = useMemo(() => {
    if (!accounts) return [];
    return accounts.map(acc => ({ id: acc.id, name: acc.name }));
  }, [accounts]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];

    let filtered = transactions.filter(transaction => {
      // Search term
      if (searchTerm && !transaction.merchant.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !transaction.category.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Category filter
      if (selectedCategory !== 'All' && transaction.category !== selectedCategory) {
        return false;
      }

      // Account filter
      if (selectedAccount !== 'All' && transaction.account_id !== selectedAccount) {
        return false;
      }

      // Type filter
      if (selectedType !== 'All') {
        if (selectedType === 'Income' && transaction.amount <= 0) return false;
        if (selectedType === 'Expense' && transaction.amount >= 0) return false;
      }

      // Date range filter
      if (dateRange !== 'All') {
        const transactionDate = new Date(transaction.date);
        const now = new Date();
        
        if (dateRange === 'This Month') {
          if (transactionDate.getMonth() !== now.getMonth() || 
              transactionDate.getFullYear() !== now.getFullYear()) return false;
        } else if (dateRange === 'Last 30 Days') {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (transactionDate < thirtyDaysAgo) return false;
        } else if (dateRange === 'Last 90 Days') {
          const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          if (transactionDate < ninetyDaysAgo) return false;
        }
      }

      return true;
    });

    // Sort transactions
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'amount':
          comparison = Math.abs(a.amount) - Math.abs(b.amount);
          break;
        case 'merchant':
          comparison = a.merchant.localeCompare(b.merchant);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [transactions, searchTerm, selectedCategory, selectedAccount, selectedType, dateRange, sortBy, sortOrder]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!filteredTransactions?.length) {
      return { income: 0, expenses: 0, net: 0, count: 0 };
    }
    
    const income = filteredTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const expenses = filteredTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    return {
      income,
      expenses,
      net: income - expenses,
      count: filteredTransactions.length
    };
  }, [filteredTransactions]);

  if (loading) return <LoadingState message="Loading transactions..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!transactions || !accounts) return null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Transactions</Text>
        <TouchableOpacity style={styles.addButton}>
          <Text style={styles.addButtonText}>+ Add Transaction</Text>
        </TouchableOpacity>
      </View>

      {/* Summary Stats */}
      <View style={styles.summaryGrid}>
        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>Total Transactions</Text>
          <Text style={styles.summaryAmount}>{summaryStats.count}</Text>
        </View>
        <View style={[styles.summaryCard, styles.incomeCard]}>
          <Text style={styles.incomeLabel}>Income</Text>
          <Text style={styles.incomeAmount}>+${summaryStats.income.toLocaleString()}</Text>
        </View>
        <View style={[styles.summaryCard, styles.expenseCard]}>
          <Text style={styles.expenseLabel}>Expenses</Text>
          <Text style={styles.expenseAmount}>-${summaryStats.expenses.toLocaleString()}</Text>
        </View>
        <View style={[styles.summaryCard, styles.netCard]}>
          <Text style={styles.netLabel}>Net</Text>
          <Text style={[
            styles.netAmount,
            summaryStats.net >= 0 ? styles.positiveNet : styles.negativeNet
          ]}>
            {summaryStats.net >= 0 ? '+' : ''}${summaryStats.net.toLocaleString()}
          </Text>
        </View>
      </View>

      {/* Filters */}
      <View style={styles.filtersCard}>
        <View style={styles.filtersGrid}>
          {/* Search */}
          <View style={styles.searchContainer}>
            <Text style={styles.filterLabel}>Search</Text>
            <TextInput
              style={styles.searchInput}
              placeholder="Search merchants, categories..."
              value={searchTerm}
              onChangeText={setSearchTerm}
            />
          </View>

          {/* Filter Buttons */}
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Category:</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScrollView}>
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  selectedCategory === 'All' ? styles.filterButtonActive : styles.filterButtonInactive
                ]}
                onPress={() => setSelectedCategory('All')}
              >
                <Text style={[
                  styles.filterButtonText,
                  selectedCategory === 'All' ? styles.filterButtonTextActive : styles.filterButtonTextInactive
                ]}>All</Text>
              </TouchableOpacity>
              {categories.slice(0, 8).map(category => (
                <TouchableOpacity
                  key={category}
                  style={[
                    styles.filterButton,
                    selectedCategory === category ? styles.filterButtonActive : styles.filterButtonInactive
                  ]}
                  onPress={() => setSelectedCategory(category)}
                >
                  <Text style={[
                    styles.filterButtonText,
                    selectedCategory === category ? styles.filterButtonTextActive : styles.filterButtonTextInactive
                  ]}>{category}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Type:</Text>
            <View style={styles.filterButtonGroup}>
              {['All', 'Income', 'Expense'].map(type => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.filterButton,
                    selectedType === type ? styles.filterButtonActive : styles.filterButtonInactive
                  ]}
                  onPress={() => setSelectedType(type)}
                >
                  <Text style={[
                    styles.filterButtonText,
                    selectedType === type ? styles.filterButtonTextActive : styles.filterButtonTextInactive
                  ]}>{type}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Date:</Text>
            <View style={styles.filterButtonGroup}>
              {['All', 'This Month', 'Last 30 Days', 'Last 90 Days'].map(range => (
                <TouchableOpacity
                  key={range}
                  style={[
                    styles.filterButton,
                    dateRange === range ? styles.filterButtonActive : styles.filterButtonInactive
                  ]}
                  onPress={() => setDateRange(range)}
                >
                  <Text style={[
                    styles.filterButtonText,
                    dateRange === range ? styles.filterButtonTextActive : styles.filterButtonTextInactive
                  ]}>{range}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        {/* Sort Controls */}
        <View style={styles.sortContainer}>
          <Text style={styles.sortLabel}>Sort by:</Text>
          <View style={styles.sortControls}>
            {['date', 'amount', 'merchant', 'category'].map(option => (
              <TouchableOpacity
                key={option}
                style={[
                  styles.sortButton,
                  sortBy === option ? styles.sortButtonActive : styles.sortButtonInactive
                ]}
                onPress={() => setSortBy(option)}
              >
                <Text style={[
                  styles.sortButtonText,
                  sortBy === option ? styles.sortButtonTextActive : styles.sortButtonTextInactive
                ]}>{option.charAt(0).toUpperCase() + option.slice(1)}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={styles.sortOrderButton}
              onPress={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            >
              <Text style={styles.sortOrderText}>
                {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Transactions List */}
      <View style={styles.transactionsCard}>
        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderText, styles.transactionColumn]}>Transaction</Text>
          <Text style={[styles.tableHeaderText, styles.dateColumn]}>Date</Text>
          <Text style={[styles.tableHeaderText, styles.accountColumn]}>Account</Text>
          <Text style={[styles.tableHeaderText, styles.amountColumn]}>Amount</Text>
          <Text style={[styles.tableHeaderText, styles.actionColumn]}>Actions</Text>
        </View>

        {/* Transactions */}
        <ScrollView style={styles.transactionsList}>
          {filteredTransactions.map((transaction) => (
            <TransactionRow
              key={transaction.id}
              transaction={transaction}
              account={accountsMap[transaction.account_id]}
            />
          ))}
        </ScrollView>

        {filteredTransactions.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateTitle}>No transactions found</Text>
            <Text style={styles.emptyStateSubtitle}>Try adjusting your filters</Text>
          </View>
        )}
      </View>

      {/* Pagination */}
      {filteredTransactions.length > 0 && (
        <View style={styles.pagination}>
          <Text style={styles.paginationText}>
            Showing {filteredTransactions.length} of {transactions.length} transactions
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1400,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  addButton: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Summary Stats
  summaryGrid: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  summaryCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  incomeCard: {
    backgroundColor: '#F0FDF4',
  },
  expenseCard: {
    backgroundColor: '#FEF2F2',
  },
  netCard: {
    backgroundColor: '#EFF6FF',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  summaryAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  incomeLabel: {
    fontSize: 14,
    color: '#059669',
    marginBottom: 4,
  },
  incomeAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#059669',
  },
  expenseLabel: {
    fontSize: 14,
    color: '#DC2626',
    marginBottom: 4,
  },
  expenseAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#DC2626',
  },
  netLabel: {
    fontSize: 14,
    color: '#2563EB',
    marginBottom: 4,
  },
  netAmount: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  positiveNet: {
    color: '#059669',
  },
  negativeNet: {
    color: '#DC2626',
  },

  // Filters
  filtersCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  filtersGrid: {
    gap: 16,
  },
  searchContainer: {
    marginBottom: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  searchInput: {
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
  },
  filterRow: {
    marginBottom: 8,
  },
  filterScrollView: {
    flexDirection: 'row',
  },
  filterButtonGroup: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    marginRight: 8,
  },
  filterButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  filterButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filterButtonTextActive: {
    color: '#FFFFFF',
  },
  filterButtonTextInactive: {
    color: '#374151',
  },
  sortContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    gap: 16,
  },
  sortLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  sortControls: {
    flexDirection: 'row',
    gap: 8,
    flex: 1,
  },
  sortButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderRadius: 8,
  },
  sortButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  sortButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  sortButtonText: {
    fontSize: 14,
  },
  sortButtonTextActive: {
    color: '#FFFFFF',
  },
  sortButtonTextInactive: {
    color: '#374151',
  },
  sortOrderButton: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
  },
  sortOrderText: {
    fontSize: 14,
    color: '#374151',
  },

  // Transactions Table
  transactionsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  transactionColumn: {
    flex: 3,
    paddingHorizontal: 12,
  },
  dateColumn: {
    flex: 2,
    paddingHorizontal: 12,
  },
  accountColumn: {
    flex: 2,
    paddingHorizontal: 12,
  },
  amountColumn: {
    flex: 2,
    paddingHorizontal: 12,
    textAlign: 'right',
  },
  actionColumn: {
    flex: 1,
    paddingHorizontal: 12,
  },
  transactionsList: {
    maxHeight: 600,
  },
  transactionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  transactionInfo: {
    flex: 3,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  transactionIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  transactionDetails: {
    flex: 1,
  },
  merchantName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  categoryName: {
    fontSize: 12,
    color: '#6B7280',
  },
  transactionMeta: {
    flex: 2,
    paddingHorizontal: 12,
  },
  transactionDate: {
    fontSize: 14,
    color: '#111827',
  },
  transactionRelativeDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  accountInfo: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  accountIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  accountName: {
    fontSize: 14,
    color: '#374151',
  },
  amountContainer: {
    flex: 2,
    alignItems: 'flex-end',
    paddingHorizontal: 12,
  },
  transactionAmount: {
    fontSize: 14,
    fontWeight: '600',
  },
  creditAmount: {
    color: '#059669',
  },
  debitAmount: {
    color: '#111827',
  },
  transactionType: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  detailsButton: {
    flex: 1,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  detailsButtonText: {
    fontSize: 14,
    color: '#2563EB',
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyStateTitle: {
    fontSize: 18,
    color: '#9CA3AF',
    marginBottom: 8,
  },
  emptyStateSubtitle: {
    fontSize: 14,
    color: '#6B7280',
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 24,
  },
  paginationText: {
    fontSize: 14,
    color: '#6B7280',
  },
});

export default Transactions; 